import { useMemo, useState } from 'react';

import { Card, CardHeader } from '../../components/ui/Card';
import { SegmentedControl } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { AsyncBoundary, Skeleton, EmptyState } from '../../components/ui/AsyncBoundary';
import { ChartCard, RANGES, rangeToWindow } from '../../components/charts/ChartCard';
import { TimeSeriesChart } from '../../components/charts/TimeSeriesChart';
import { useResource } from '../../hooks/useResource';
import { useAuth } from '../../auth/AuthProvider';
import * as fmt from '../../lib/format';

/**
 * History: four small multiples over one shared range.
 *
 * Speed, temperature, signal and fix quality are separate charts rather than
 * one chart with four lines and two axes. They have different units and
 * different scales, and putting them on a shared axis would invent
 * relationships between them that the data does not contain.
 */
export function HistoryTab({ vehicleId }) {
  const [range, setRange] = useState('24h');
  const { historyWindowDays } = useAuth();

  const window = useMemo(() => rangeToWindow(range), [range]);

  const history = useResource(`/api/vehicles/${vehicleId}/telemetry`, {
    from: window.from,
    to: window.to,
    resolution: 'auto',
  });

  const rangeControl = (
    <SegmentedControl
      value={range}
      onChange={setRange}
      options={RANGES.filter(
        // A viewer can only read 30 days, so do not offer a range that will be
        // silently clipped — offer what they can actually have.
        (option) => !historyWindowDays || option.ms <= historyWindowDays * 86400e3
      ).map((option) => ({ value: option.value, label: option.label }))}
    />
  );

  return (
    <AsyncBoundary state={history} skeleton={<Skeleton className="h-96 w-full" />}>
      {(data) => {
        const series = data.series || [];

        if (!series.length) {
          return (
            <Card>
              <CardHeader title="History" actions={rangeControl} icon="history" />
              <EmptyState
                icon="chart"
                title="No readings in this range"
                description="The device may have been offline, or the range may predate its first packet."
              />
            </Card>
          );
        }

        const points = (key) =>
          series.map((row) => ({ t: new Date(row.t).getTime(), value: numeric(row[key]) }));

        const tableRows = series.slice(-200).map((row, index) => ({
          key: index,
          t: fmt.dateTime(row.t),
          speed: row.speed_kph === null ? '—' : Number(row.speed_kph).toFixed(1),
          temp: row.temp_c === null ? '—' : Number(row.temp_c).toFixed(1),
          signal: row.gsm_signal ?? '—',
          sats: row.sats ?? '—',
        }));

        return (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                {rangeControl}
                <Badge tone="neutral" size="sm" icon={null}>
                  {data.resolution === 'raw' ? 'Full resolution' : `${data.resolution} averages`}
                </Badge>
                <span className="text-[12.5px] text-ink-3 tabular-nums">
                  {fmt.number(data.points)} points
                </span>
              </div>

              {data.clampedToWindow && (
                <Badge tone="warning" size="sm">
                  Limited to the last {data.clampedToWindow.days} days for your role
                </Badge>
              )}
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <ChartCard
                title="Speed"
                subtitle={`${window.label} · km/h`}
                icon="arrow"
                table={{
                  columns: [
                    { key: 't', header: 'Time' },
                    { key: 'speed', header: 'km/h', align: 'right' },
                  ],
                  rows: tableRows,
                }}
              >
                <TimeSeriesChart
                  ariaLabel="Vehicle speed over time"
                  area
                  yMin={0}
                  height={210}
                  formatValue={(v) => String(Math.round(v))}
                  series={[{ key: 'speed', label: 'Speed', points: points('speed_kph'), format: fmt.speed }]}
                />
              </ChartCard>

              <ChartCard
                title="Temperature and humidity"
                subtitle={`${window.label} · sensor readings`}
                icon="thermometer"
                table={{
                  columns: [
                    { key: 't', header: 'Time' },
                    { key: 'temp', header: '°C', align: 'right' },
                  ],
                  rows: tableRows,
                }}
              >
                <TimeSeriesChart
                  ariaLabel="Temperature and humidity over time"
                  height={210}
                  formatValue={(v) => String(Math.round(v))}
                  series={[
                    { key: 'temp', label: 'Temperature (°C)', points: points('temp_c'), format: fmt.temperature },
                    {
                      key: 'humidity',
                      label: 'Humidity (%)',
                      points: points('humidity_pct'),
                      format: (v) => fmt.percent(v, 0),
                    },
                  ]}
                />
              </ChartCard>

              <ChartCard
                title="Cellular signal"
                subtitle={`${window.label} · CSQ 0–31`}
                icon="signal"
                table={{
                  columns: [
                    { key: 't', header: 'Time' },
                    { key: 'signal', header: 'CSQ', align: 'right' },
                  ],
                  rows: tableRows,
                }}
              >
                <TimeSeriesChart
                  ariaLabel="Cellular signal strength over time"
                  area
                  yMin={0}
                  height={210}
                  series={[{ key: 'gsm', label: 'Signal', points: points('gsm_signal') }]}
                />
              </ChartCard>

              <ChartCard
                title="GNSS fix quality"
                subtitle={`${window.label} · satellites in view`}
                icon="satellite"
                table={{
                  columns: [
                    { key: 't', header: 'Time' },
                    { key: 'sats', header: 'Satellites', align: 'right' },
                  ],
                  rows: tableRows,
                }}
              >
                <TimeSeriesChart
                  ariaLabel="Satellites in view over time"
                  area
                  yMin={0}
                  height={210}
                  series={[{ key: 'sats', label: 'Satellites', points: points('sats') }]}
                />
              </ChartCard>
            </div>
          </div>
        );
      }}
    </AsyncBoundary>
  );
}

function numeric(value) {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}
